// ============================================================================
// JARVISTRAVEL - BUDGET API
// Version: 1.1.0
// Budget and budget category CRUD + the budget write path (JAR-791)
// ============================================================================
//
// Failure contract (api/AGENTS.md, JAR-450): these are all writes, so every
// function propagates. A budget write that reports success it did not achieve
// is the failure mode that matters here — the user retypes their allocations,
// sees them on screen, reloads, and finds the old numbers. `ApiRequestError` is
// re-thrown intact so callers can still tell "never reached the backend" from
// "the backend said no".

import { ApiRequestError, api, isDemoSession } from "./client";
import { reportApiFailure } from "./failure";
import { isBackendTripID } from "@/app/utils/tripIds";
import type { PaymentMethod } from "@/app/types";

export interface CreateBudgetRequest {
  total_budget: number;
  currency?: string;
}

export interface BudgetResponse {
  id: string;
  trip_id: string;
  total_budget: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBudgetCategoryRequest {
  category: string;
  allocated: number;
  color: string;
}

export interface BudgetCategoryResponse {
  id: string;
  category: string;
  allocated: number;
  color: string;
}

/** An expense as the backend records it (`POST /trips/{id}/expenses`). */
export interface ExpenseResponse {
  id: string;
  category: string;
  description: string;
  amount: number;
  date: string;
}

/**
 * Rethrow the transport error when there is one, else fail on an empty body.
 *
 * Preserves `ApiRequestError` rather than flattening it into `new Error(msg)`:
 * the class carries `code`, `detailCode` and `isNetworkError`, and `userMessage`
 * needs them to say "you're offline" instead of pasting a raw transport string
 * into a toast.
 */
function failWrite(scope: string, error: unknown): never {
  reportApiFailure(scope, error);
  if (error) throw error;
  throw new Error("Empty response from server");
}

export async function createBudget(tripId: string, totalBudget: number, currency = "USD"): Promise<BudgetResponse> {
  const result = await api.post<BudgetResponse>(`/trips/${encodeURIComponent(tripId)}/budget`, {
    total_budget: totalBudget,
    currency,
  });

  if (result.error || !result.data) {
    failWrite("[budget] create failed:", result.error);
  }
  return result.data;
}

export async function createBudgetCategory(tripId: string, category: string, allocated: number, color: string): Promise<BudgetCategoryResponse> {
  const result = await api.post<BudgetCategoryResponse>(`/trips/${encodeURIComponent(tripId)}/budget/categories`, {
    category,
    allocated,
    color,
  });

  if (result.error || !result.data) {
    failWrite("[budget] create category failed:", result.error);
  }
  return result.data;
}

/**
 * Set the trip's total budget.
 *
 * NOTE: the backend's `UpdateBudget` changes `total_budget` and leaves the
 * categories exactly as they were, so after this call the category allocations
 * no longer sum to the total. Callers that change the total must also send the
 * category allocations that go with it (see `saveBudgetAllocations`). A
 * top-down "raise the total, redistribute the categories" endpoint is tracked
 * separately.
 */
export async function updateBudgetTotal(tripId: string, totalBudget: number, currency?: string): Promise<BudgetResponse> {
  const body: CreateBudgetRequest = { total_budget: totalBudget };
  if (currency) body.currency = currency;

  const result = await api.put<BudgetResponse>(`/trips/${encodeURIComponent(tripId)}/budget`, body);

  if (result.error || !result.data) {
    failWrite("[budget] update total failed:", result.error);
  }
  return result.data;
}

/** Set one category's allocation. `categoryId` is the backend row UUID. */
export async function updateCategoryAllocation(tripId: string, categoryId: string, allocated: number): Promise<BudgetCategoryResponse> {
  const result = await api.put<BudgetCategoryResponse>(
    `/trips/${encodeURIComponent(tripId)}/budget/categories/${encodeURIComponent(categoryId)}`,
    { allocated },
  );

  if (result.error || !result.data) {
    failWrite("[budget] update category failed:", result.error);
  }
  return result.data;
}

/**
 * Persist an edited allocation set: every changed category, then the new total.
 *
 * Categories go first, and that ordering buys exactly one thing: the total is
 * never raised over categories that failed to hold it, so the budget never
 * reads as under-spent. That is the ONLY ordering this function owes anyone,
 * which is why the categories themselves go out concurrently — seven separate
 * rows, seven independent writes, and nothing downstream reads the order they
 * landed in. `Promise.all` keeps the one guarantee that matters: it rejects on
 * the first failure, so `updateBudgetTotal` is never reached.
 *
 * **It is not atomic, and nothing here makes it so.** There is no transaction
 * across these requests. A failure leaves some categories already written at
 * their new amounts while `total_budget` keeps its old value — a mix of old and
 * new allocations that no longer sums to the total. The caller sees a rejected
 * promise and can tell the user their previous total stands, which is true; the
 * categories underneath it may not.
 *
 * Which categories landed is now arbitrary rather than a prefix of the list.
 * That costs almost nothing: `updateCategoryAllocation` is a PUT carrying an
 * absolute amount, so pressing save again rewrites all of them whatever the
 * first attempt managed. No caller reads the partial state and no recovery path
 * branches on it — a prefix was never more recoverable than a subset, only
 * easier to picture.
 *
 * The "almost" is a race the sequential version could not have (review M1).
 * `Promise.all` rejects on the FIRST failure but does not cancel the siblings —
 * there is no AbortController through `client.request`, only its timeout — so
 * unfinished writes from the failed attempt may still be in the air when the
 * caller catches, re-enables Save, and the user presses it again. Each write is
 * last-value-wins, so a straggler from attempt 1 can land after attempt 2 and
 * put a stale amount back on one category. Rare, and nothing here can report it:
 * the toast has already been shown. The single transactional endpoint below is
 * the only real fix; `Promise.allSettled` is not, since it would only make the
 * failure path wait for every straggler before telling the user anything.
 *
 * One consequence for whoever reads a failure: the error that surfaces is the
 * first to REJECT, not the first in the list. With concurrent writes that is
 * decided by timing, so do not treat it as naming a particular category.
 *
 * The honest guarantee is therefore "no raised ceiling with nothing under it",
 * not "all or nothing". Do not build on this expecting a rollback. Closing the
 * gap properly needs one backend endpoint that takes the whole allocation set
 * and writes it in a single transaction.
 *
 * A category without a `backendId` has no row to update. On a trip core stores
 * it is given one here, with its amount. That is Flights or Hotels on a trip
 * that gained a flight or a stay after its budget was built: the budget screen
 * shows the category at $0 for the traveler to fund (JAR-2624), and no row was
 * made for it when the trip was saved. These used to be skipped, so the amount
 * typed was dropped and the screen went back to $0 on the next read (JAR-2880).
 *
 * No row is made for $0: a category with no row already reads as $0. Each
 * such category's row id comes back keyed by category, whether this save made
 * the row or found it already there, so the caller can keep it and its next
 * save updates that row. Every one of them is in the answer, or the save
 * failed: there is no answer that leaves one out. A trip core does not store (the demo, or one
 * kept on the device) has no budget there to add a row to, and is left alone.
 */
export async function saveBudgetAllocations(
  tripId: string,
  categories: { id: string; color: string; backendId?: string; allocated: number }[],
  totalBudget: number,
): Promise<{ rowIds: Record<string, string> }> {
  const stored = categories.filter((c) => Boolean(c.backendId));
  const fresh = isBackendTripID(tripId) ? categories.filter((c) => !c.backendId && c.allocated > 0) : [];
  const rowIds: Record<string, string> = {};

  await Promise.all([
    ...stored.map((cat) => updateCategoryAllocation(tripId, cat.backendId!, cat.allocated)),
    ...fresh.map(async (cat) => { rowIds[cat.id] = await ensureCategoryRow(tripId, cat); }),
  ]);
  await updateBudgetTotal(tripId, totalBudget);

  return { rowIds };
}

/**
 * Gives a category its row on core at the given amount, and answers the row's id.
 *
 * Core refuses a second row of the same name with a 409. That means the row is
 * already there and this screen doesn't know its id: an earlier save made it and
 * then failed on another write, or another device made it. So the row is looked
 * up and set to the amount, which is what the traveler asked for either way.
 * Without this a save that half-failed once would keep failing until a reload.
 */
async function ensureCategoryRow(tripId: string, cat: { id: string; color: string; allocated: number }): Promise<string> {
  try {
    return (await createBudgetCategory(tripId, cat.id, cat.allocated, cat.color)).id;
  } catch (e) {
    if (!(e instanceof ApiRequestError) || e.status !== 409) throw e;
    const rows = await api.get<{ categories: BudgetCategoryResponse[] | null }>(`/trips/${encodeURIComponent(tripId)}/budget/categories`);
    const row = rows.data?.categories?.find((r) => r.category === cat.id);
    if (!row) throw e;
    await updateCategoryAllocation(tripId, row.id, cat.allocated);
    return row.id;
  }
}

/** An expense as the budget's form records it. */
interface ExpenseInput {
  category: string;
  description: string;
  amount: number;
  date: string;
  currency?: string;
  notes?: string;
  method?: PaymentMethod;
}

// One body for adding an expense and changing one, so the two can't drift. A
// note is sent whenever it's given, since an empty one clears it (JAR-2073).
const expenseBody = (e: ExpenseInput) => ({
  category: e.category,
  description: e.description,
  amount: e.amount,
  date: e.date,
  ...(e.currency ? { currency: e.currency } : {}),
  ...(e.notes !== undefined ? { notes: e.notes } : {}),
  ...(e.method !== undefined ? { payment_method: e.method } : {}),
});

/** Record a manual expense against the trip budget. */
export async function createExpense(tripId: string, expense: ExpenseInput): Promise<ExpenseResponse> {
  const result = await api.post<ExpenseResponse>(`/trips/${encodeURIComponent(tripId)}/expenses`, expenseBody(expense));

  if (result.error || !result.data) {
    failWrite("[budget] create expense failed:", result.error);
  }
  return result.data;
}

/** Change a recorded expense to what the expense form now holds (JAR-2048). */
export async function updateExpense(tripId: string, expenseId: string, expense: ExpenseInput): Promise<void> {
  const result = await api.put(`/trips/${encodeURIComponent(tripId)}/expenses/${encodeURIComponent(expenseId)}`, expenseBody(expense));
  if (result.error) failWrite("[budget] update expense failed:", result.error);
}

/** Delete a recorded expense (JAR-2048). */
export async function deleteExpense(tripId: string, expenseId: string): Promise<void> {
  const result = await api.del(`/trips/${encodeURIComponent(tripId)}/expenses/${encodeURIComponent(expenseId)}`);
  if (result.error) failWrite("[budget] delete expense failed:", result.error);
}

/**
 * Change an actual expense, such as a charge confirmed from the bank (JAR-2074).
 * It keeps its own currency, which the form doesn't change.
 */
export async function updateActualExpense(tripId: string, expenseId: string, expense: Omit<ExpenseInput, "currency" | "notes" | "method">): Promise<void> {
  const result = await api.patch(`/trips/${encodeURIComponent(tripId)}/expenses/${encodeURIComponent(expenseId)}`, {
    merchant: expense.description,
    amount_minor: toMinor(expense.amount),
    category: expense.category,
    // Core reads a timestamp and keeps its calendar day.
    occurred_at: `${expense.date}T00:00:00Z`,
  });
  if (result.error) failWrite("[budget] update actual expense failed:", result.error);
}

/** Take an actual expense off the budget (JAR-2074). Core keeps the row, marked excluded. */
export async function excludeActualExpense(tripId: string, expenseId: string): Promise<void> {
  const result = await api.post(`/trips/${encodeURIComponent(tripId)}/expenses/${encodeURIComponent(expenseId)}/exclude`);
  if (result.error) failWrite("[budget] exclude actual expense failed:", result.error);
}

// ============================================================================
// Budget rebalance (JAR-794 · backend JAR-792)
// ============================================================================
//
// Setting a new trip total redistributes the categories to match it. The
// arithmetic lives in the backend because it is an invariant — the categories
// must sum to the total — not a view concern.
//
// The share each category absorbs is proportional to what it has *free*, not to
// its whole allocation: money already spent or committed is committed, and a
// flight booked at 2,400 must not become 3,600 because the traveller raised
// their ceiling.

// Mirrors core's RebalanceOutcome (rebalance.go:47-63) exactly. `unchanged` was
// missing here while the server had been sending it (JAR-864 N2), so a category
// the server had RAISED to its floor was rendered under whatever label the UI
// mapped an unknown string to — core's own comment records the symptom: a floor
// that "read held_committed while its number visibly rose".
//
// A union that is a subset of what the server sends does not fail loudly; it
// mislabels, and the label is the only thing telling the traveller why their
// money moved.
export type RebalanceOutcome = "scaled" | "held_committed" | "unchanged";

/** One category's before/after, with the reason it did or didn't move. */
export interface CategoryRebalance {
  category_id: string;
  category: string;
  from_minor: number;
  to_minor: number;
  committed_minor: number;
  outcome: RebalanceOutcome;
}

export interface RebalanceResult {
  preview: boolean;
  total_budget: number;
  previous_total: number;
  categories: CategoryRebalance[];
  committed_minor: number;
  /** Money the rebalancer could not place — only when every category is fully
   *  committed, so there is no flexible share to scale. */
  unallocated_minor: number;
}

/** A category as the demo solver sees it. Major units, as the FE stores them. */
export interface LocalRebalanceCategory {
  id: string;
  name: string;
  allocated: number;
  committed: number;
}

/** Raised when the requested total is below what is already spent or committed. */
export class BudgetFloorError extends Error {
  readonly code = "BELOW_COMMITTED";
  constructor(message: string) {
    super(message);
    this.name = "BudgetFloorError";
  }
}

const toMinor = (major: number) => Math.round(major * 100);
export const toMajor = (minor: number) => minor / 100;

/**
 * Finite in dollars is not the same as finite in cents.
 *
 * `Number.isFinite(2e306)` is true and `2e306 * 100` is Infinity, so a total one
 * input step below the overflow the finite check closes still put Infinity into
 * `to_minor` and wrote `allocated: Infinity` through onUpdateBudget. The
 * multiplication is where this actually breaks, so that is what gets checked.
 */
const isUsableTotal = (major: number) => Number.isFinite(major) && Number.isFinite(toMinor(major));

/**
 * The demo trip's rebalance, computed on the device.
 *
 * A demo session has no backend rows, so the server cannot answer — and a blank
 * preview would make the feature look broken in the only environment most
 * people ever run. This mirrors the Go solver (services/trip-core/internal/
 * budgeting/rebalance.go); `rebalanceBudget.demo.test.ts` pins it to the same
 * worked example as the Go test, so if either side drifts a test fails rather
 * than the demo quietly telling a different story than production.
 *
 * Reachable only through the isDemoSession() guard below — never from a live
 * session, where a fabricated budget would be indistinguishable from the real
 * one.
 */
export function solveRebalanceLocally(
  categories: LocalRebalanceCategory[],
  totalBudget: number,
  previousTotal: number,
): RebalanceResult {
  // Finite first, because the floor check below cannot refuse what it cannot
  // compare. `NaN < totalCommitted` is false, so a NaN total sails past the one
  // guard that exists to stop a rebalance cutting into committed money — the
  // opposite of failing closed. Infinity is reachable too: the number input
  // accepts "1e309", which parseFloat turns into Infinity and the caller's
  // `total > 0` check waves through; Infinity - Infinity then puts NaN into
  // to_minor and unallocated_minor, and the preview renders it.
  if (!isUsableTotal(totalBudget)) {
    throw new Error("Budget total must be a finite number");
  }

  const target = toMinor(totalBudget);
  const rows = categories.map((c) => ({
    ...c,
    allocatedMinor: toMinor(c.allocated),
    committedMinor: toMinor(c.committed),
  }));

  const totalCommitted = rows.reduce((sum, r) => sum + r.committedMinor, 0);
  if (target < totalCommitted) {
    const largest = rows.reduce((a, b) => (b.committedMinor > a.committedMinor ? b : a));
    throw new BudgetFloorError(
      `That's below the ${formatUsd(toMajor(totalCommitted))} you've already spent or committed. ${largest.name} alone accounts for ${formatUsd(toMajor(largest.committedMinor))}.`,
    );
  }

  const flexOf = (r: { allocatedMinor: number; committedMinor: number }) =>
    Math.max(0, r.allocatedMinor - r.committedMinor);
  const totalFlex = rows.reduce((sum, r) => sum + flexOf(r), 0);
  const pool = target - totalCommitted;

  const out: CategoryRebalance[] = rows.map((r) => ({
    category_id: r.id,
    category: r.name,
    from_minor: r.allocatedMinor,
    to_minor: r.committedMinor,
    committed_minor: r.committedMinor,
    // Provisional. The real label is assigned AFTER the distribution below,
    // because it depends on where to_minor ends up — see the note there.
    outcome: "scaled",
  }));

  if (totalFlex > 0) {
    // Largest-remainder, matching the server: flooring every share and dropping
    // the remainders leaves the categories a few cents short of the total.
    const remainders: { idx: number; frac: number }[] = [];
    let distributed = 0;
    rows.forEach((r, i) => {
      const flex = flexOf(r);
      if (flex === 0) return;
      const exact = (flex * pool) / totalFlex;
      const whole = Math.floor(exact);
      out[i].to_minor += whole;
      distributed += whole;
      remainders.push({ idx: i, frac: exact - whole });
    });

    let leftover = pool - distributed;
    remainders.sort((a, b) => b.frac - a.frac || a.idx - b.idx);
    for (let i = 0; i < remainders.length && leftover > 0; i++) {
      out[remainders[i].idx].to_minor++;
      leftover--;
    }
  }

  // Label from WHAT HAPPENED TO THE NUMBER, not from what the inputs suggested
  // — mirroring core's outcomeFor (rebalance.go:238) term for term (JAR-864 N2).
  //
  // This used to be assigned above, before the distribution ran, so a category
  // whose to_minor was about to change was already labelled from its inputs.
  // Core fixed the same bug server-side in core#362 L2, and its reason is the
  // one that matters here: the label is rendered next to the diff, so a label
  // disagreeing with the numbers beside it is worse than no label. A category
  // raised to its floor read "Stays put" while its figure visibly rose.
  //
  // `unchanged` is the case that had nowhere to go before: the number did not
  // move AND that was not because it was pinned by a commitment.
  out.forEach((row, i) => {
    const pinned = flexOf(rows[i]) === 0 && rows[i].committedMinor > 0;
    if (row.to_minor === row.from_minor) {
      row.outcome = pinned ? "held_committed" : "unchanged";
      return;
    }
    row.outcome = "scaled";
  });

  const allocated = out.reduce((sum, r) => sum + r.to_minor, 0);
  return {
    preview: true,
    total_budget: totalBudget,
    previous_total: previousTotal,
    categories: out,
    committed_minor: totalCommitted,
    unallocated_minor: target - allocated,
  };
}

// Whole dollars, except when rounding to them would say $0 about money that
// exists — "below the $0 you've already spent" is a refusal that reads as a bug.
function formatUsd(amount: number): string {
  const cents = amount !== 0 && Math.abs(amount) < 1;
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents ? 2 : 0,
  });
}

/**
 * Set a new trip total and redistribute the categories.
 *
 * Pass `preview: true` to see the result without writing it — the backend runs
 * the same code path either way, so the preview cannot disagree with what lands.
 *
 * `local` is used only in a demo session; a live session always asks the server.
 */
export async function rebalanceBudget(
  tripId: string,
  totalBudget: number,
  opts: { preview?: boolean; local?: { categories: LocalRebalanceCategory[]; previousTotal: number } } = {},
): Promise<RebalanceResult> {
  const preview = opts.preview ?? false;

  // Checked here as well as in the local solver, so a live session cannot POST
  // a non-finite total either. `JSON.stringify(Infinity)` is `null`, so the
  // backend would receive `{"total_budget": null}` and answer about a request
  // nobody made.
  if (!isUsableTotal(totalBudget)) {
    throw new Error("Budget total must be a finite number");
  }

  if (isDemoSession()) {
    if (!opts.local) throw new Error("Demo rebalance needs local categories");
    return { ...solveRebalanceLocally(opts.local.categories, totalBudget, opts.local.previousTotal), preview };
  }

  const result = await api.post<RebalanceResult>(
    `/trips/${encodeURIComponent(tripId)}/budget/rebalance`,
    { total_budget: totalBudget, preview },
  );

  if (result.error) {
    // The floor refusal is actionable — the message names the amount and the
    // category holding it — so it is surfaced rather than flattened into a
    // generic failure.
    if (result.error.code === "BELOW_COMMITTED" || result.error.detailCode === "BELOW_COMMITTED") {
      throw new BudgetFloorError(result.error.message);
    }
    throw result.error;
  }
  if (!result.data) throw new Error("Empty response from server");
  return result.data;
}
